// True iff the voxel at (x, y, z) is on the cube's outer surface.
export function isSurface(x, y, z, N) {
    return x === 0 || x === N - 1 || y === 0 || y === N - 1 || z === 0 || z === N - 1;
}
// Sparse cell allocation: only surface voxels get a real CubeCell. Interior slots are stored as
// `null` (cast through `unknown` to fit the dense type). This is safe because every caller —
// generation, flood fill, win check, rendering — only ever accesses surface positions (filtered
// by `isSurface` or visited via `forEachSurfaceVoxel`). Saves ~46% cell objects at N=15 and
// ~21% at N=5; useful at higher levels and harmless at low ones.
export function createEmptyVoxelCube(N) {
    return Array.from({ length: N }, (_, x) => Array.from({ length: N }, (_, y) => Array.from({ length: N }, (_, z) => isSurface(x, y, z, N)
        ? { isMine: false, isRevealed: false, isFlagged: false, adjacentMines: 0 }
        : null)));
}
// 26-Moore neighbors of (x, y, z), filtered to in-bounds AND surface. The interior diagonal
// neighbor is excluded because it can never hold a (surface-only) mine and the player would
// never see it anyway.
export function getSurfaceVoxelNeighbors(x, y, z, N) {
    const result = [];
    for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
            for (let dz = -1; dz <= 1; dz++) {
                if (dx === 0 && dy === 0 && dz === 0)
                    continue;
                const nx = x + dx, ny = y + dy, nz = z + dz;
                if (nx < 0 || nx >= N || ny < 0 || ny >= N || nz < 0 || nz >= N)
                    continue;
                if (!isSurface(nx, ny, nz, N))
                    continue;
                result.push({ x: nx, y: ny, z: nz });
            }
        }
    }
    return result;
}
// Iterates surface voxels in row-major (x, y, z) order.
export function forEachSurfaceVoxel(cube, fn) {
    const N = cube.length;
    for (let x = 0; x < N; x++) {
        for (let y = 0; y < N; y++) {
            for (let z = 0; z < N; z++) {
                if (!isSurface(x, y, z, N))
                    continue;
                fn(cube[x][y][z], { x, y, z });
            }
        }
    }
}
// Count of surface voxels in an NxNxN cube: outer total minus interior (N-2)³.
// Closed form: N=1 → 1; N=2 → 8 (every cell is surface); else N³ − (N−2)³ = 6N² − 12N + 8.
export function surfaceCount(N) {
    if (N <= 0)
        return 0;
    if (N === 1)
        return 1;
    if (N === 2)
        return 8;
    return N * N * N - (N - 2) * (N - 2) * (N - 2);
}
